import { CommonModule, NgClass } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  Signal,
  signal,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  CompleteAppointmentService,
  DeleteAppointmentModalComponent,
  DeleteAppointmentModalComponentData,
  DeleteAppointmentModalComponentResult,
  SchedulesService,
} from '@libs/schedule';
import {
  appointmentHasParallelAppointments,
  createAppointmentActions,
  DEFAULT_START_HOUR,
  END_HOUR,
  INTERVAL_COUNT,
  ScheduleCalendarElement,
  ScheduleCalendarTime,
  ScheduleCalendarTimeWithOperatory,
  ScheduleOperatoryProviderAppointment,
  SHOWCASE_SCROLL_START_HOUR,
  START_HOUR,
  AppointmentActionType,
  selectAppointmentActionType,
  selectIsEaglesoftPms,
  selectStatusOptions,
  selectCompletedAppointmentStatusId,
  selectTeeth,
  TIME_COLUMN_INTERVAL_COUNT,
  patientActions,
  selectSiteSettingsTimezone,
  NurturingFeature,
} from '@libs/store';
import { adjustedMinuteTimer$, TypedChanges, routes, HourToDayPartPipe, HourToDayPartHourPipe } from '@libs/utils';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs';
import { NavigationService, PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import { NurturingDirective, NurturingStore } from '@libs/nurturing';
import { PatientDetailsV2 } from '@ffd/api-client';
import { Dialog } from '@angular/cdk/dialog';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { HealthHistoryDialogComponent, HealthHistoryDialogData } from '@libs/home';
import { AddTreatmentItem } from '@libs/ui-lib';
import { PadStartPipe } from '../pad-start';
import { ScheduleTimeToGridRowPipe } from '../schedule-time-to-grid-row';
import { ScheduleCalendarTimeColumnComponent } from './schedule-calendar-time-column';
import { ScheduleCalendarMyDayComponent } from './schedule-calendar-my-day';
import { ScheduleCalendarItemComponent } from './schedule-calendar-item';
import { ScheduleCalendarHeaderComponent } from './schedule-calendar-header';
import { RowsSeparatorsAndTimeIndicatorComponent } from './rows-separators-and-time-indicator';
import { ScheduleCalendarVM } from './schedule-calendar.vm';

const TIME_COLUMN_WIDTH = 48;
const MY_DAY_COLUMN_WIDTH = 48;
const COLUMN_GAP = 4;
const ROW_HEIGHT = 10;
const ROW_HEIGHT_PX = '10px';
const DEFAULT_NUMBER_OF_OPERATORIES = 5;
const NUMBER_OF_COLUMN_LOADING_SKELETONS = DEFAULT_NUMBER_OF_OPERATORIES;
const NUMBER_OF_ROW_LOADING_SKELETONS = 16;
const SCROLL_INDICATOR_WIDTH = 20;
const MIN_WIDTH_OF_OPERATORIES = 68;
const COMPACT_VIEW_MIN_WIDTH_OF_OPERATORIES = 32;
const CLICK_DEBOUNCE_MS = 250;

/** Total number of guided tour steps, for the "1 of 3" counter. */
const GUIDED_TOUR_TOTAL_STEPS = 3;

/**
 * Headroom left above the anchored appointment when auto-scrolling to it, so the coachmark fits
 * above rather than being flipped below. Covers the popover height plus its caret and gap.
 */
const GUIDED_TOUR_SCROLL_OFFSET_Y = 180;
const GUIDED_TOUR_SCROLL_OFFSET_X = 100;

@Component({
  selector: 'lib-schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrl: './schedule-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    HourToDayPartPipe,
    HourToDayPartHourPipe,
    ScheduleTimeToGridRowPipe,
    ScheduleCalendarItemComponent,
    ScheduleCalendarTimeColumnComponent,
    ScheduleCalendarHeaderComponent,
    ScheduleCalendarMyDayComponent,
    RowsSeparatorsAndTimeIndicatorComponent,
    PadStartPipe,
    TranslateModule,
    NgClass,
    PushTagOnClickDirective,
    NurturingDirective,
    CdkScrollable,
  ],
})
export class ScheduleCalendarComponent implements OnChanges, OnDestroy, AfterViewInit {
  protected readonly renderer = inject(Renderer2);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly schedulesService = inject(SchedulesService);
  private readonly completeAppointmentService = inject(CompleteAppointmentService);
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly dialog = inject(Dialog);
  private readonly nurturingStore = inject(NurturingStore);
  private readonly navigationService = inject(NavigationService);
  private readonly injector = inject(Injector);

  @Input() public myDay: ScheduleCalendarElement[] = [];
  @Input() public operatories: ScheduleCalendarVM[] = [];
  @Input() public selectedAppointmentId?: string;
  @Input() public selectedEventId?: string;
  @Input() public isShowcase: boolean = false;
  @Input() public isToday: boolean = false;
  @Input() public isScheduleLoadingSignal: Signal<boolean> = signal(false);
  @Input() public isCompactViewSignal: Signal<boolean> = signal(false);
  @Input() public hidePatientNameSignal: Signal<boolean> = signal(false);
  @Input() public showTentativeSignal: Signal<boolean> = signal(false);
  @Input() public areTreatmentsLoadedSignal: boolean = false;
  public containerWidth = signal(0);
  protected readonly scrollIndicatorWidth = SCROLL_INDICATOR_WIDTH;

  @Output() public appointmentSelect = new EventEmitter<string>();
  @Output() public appointmentClick = new EventEmitter<string>();
  @Output() public appointmentDoubleClick = new EventEmitter();
  @Output() public newAppointment = new EventEmitter<ScheduleCalendarTimeWithOperatory>();
  @Output() public eventClick = new EventEmitter<string>();

  @ViewChild('rowsSeparatorsAndTimeIndicator') private readonly rowsSeparatorsAndTimeIndicator!: RowsSeparatorsAndTimeIndicatorComponent;
  @ViewChildren('myDayCalendarItem') private readonly myDayCalendarItems!: QueryList<ScheduleCalendarItemComponent>;
  @ViewChildren(ScheduleCalendarItemComponent) private readonly allCalendarItems!: QueryList<ScheduleCalendarItemComponent>;

  protected readonly appointmentActionType = this.store.selectSignal(selectAppointmentActionType);
  protected readonly statusOptions = this.store.selectSignal(selectStatusOptions);
  protected readonly completedStatusId = this.store.selectSignal(selectCompletedAppointmentStatusId);
  protected readonly isEditAppointment = computed(() => this.appointmentActionType() === AppointmentActionType.Edit);
  protected readonly isEaglesoftPms = this.store.selectSignal(selectIsEaglesoftPms);

  /**
   * Guided tour coachmark, anchored to one of the logged-in user's appointments (see
   * {@link guidedTourAppointmentId}). This is the tour's first step; its "Next" button dismisses the
   * step and opens the patient's chart, where the following steps pick up.
   */
  protected readonly guidedTourFeature = NurturingFeature.GuidedTourStep1AppointmentNudge;
  protected readonly guidedTourStep = 1;
  protected readonly guidedTourTotalSteps = GUIDED_TOUR_TOTAL_STEPS;

  /**
   * Real patient appointments on the loaded day, from every operatory — the tour anchor may belong to
   * any provider, not just the logged-in one. Sourced from the grid (rather than `myDay`) so the
   * candidate is guaranteed to be an appointment the directive actually renders a host for.
   *
   * Excludes anything the coachmark could not sensibly point at: blocks with no patient, and
   * pre-registration, provisional, mid-edit or hidden-tentative appointments. The `isEdited` and
   * tentative rules mirror the grid's own render guard, so a candidate is always on screen; the
   * `patientId` and pre-registration rules mirror the Chart button's guard, since the tour's next
   * step opens that patient's chart.
   */
  private readonly guidedTourCandidates = computed<ScheduleCalendarElement[]>(() => {
    const showTentative = this.showTentativeSignal();

    return this.operatoriesSignal()
      .flatMap((operatory) => operatory.appointments)
      .filter(
        (appointment) =>
          !!appointment.patientId &&
          !appointment.isPreRegistrationAppointment &&
          !appointment.isProvisional &&
          !appointment.isEdited &&
          (!appointment.isTentative || showTentative),
      );
  });

  /**
   * The appointment the coachmark is anchored to, picked from {@link guidedTourCandidates} by priority:
   *
   * 1. the logged-in user's own appointment, in the future
   * 2. the logged-in user's own appointment, in the past
   * 3. another provider's appointment, in the future
   * 4. another provider's appointment, in the past
   *
   * "Own" means the appointment is in the user's My Day list. "Future" means it starts after now; on a
   * past day every appointment is in the past, on a future day every appointment is in the future.
   * Within a tier the one closest to now wins: the earliest upcoming, or the latest past one.
   *
   * Note the calendar only holds the selected date's appointments, so the coachmark appears on another
   * day only once the user navigates to it; days that were never fetched cannot be searched from here.
   */
  protected readonly guidedTourAppointmentId = computed<string | undefined>(() => {
    const candidates = this.guidedTourCandidates();

    if (candidates.length === 0) {
      return;
    }

    const toMinutes = (time: ScheduleCalendarTime): number => time.hour * 60 + time.minute;

    // `updateCount` ticks every minute; reading it re-runs this as the clock advances. The cutoffs
    // use a plain wall-clock reading, since `currentTimeWithTimeZone` is offset by an hour for
    // grid-row math and would skew the comparison.
    this.updateCount();
    const now = DateTime.local();
    const selectedDayDiff = this.schedulesService.viewModel().selectedDate.startOf('day').diff(now.startOf('day'), 'days').days;
    const nowMinutes = now.hour * 60 + now.minute;
    const ownIds = this.myDayIds();

    const isFuture = (appointment: ScheduleCalendarElement): boolean => {
      if (selectedDayDiff !== 0) {
        return selectedDayDiff > 0;
      }

      return toMinutes(appointment.from) >= nowMinutes;
    };

    // Lower rank wins: own before other first, then future before past.
    const rank = (appointment: ScheduleCalendarElement): number => (ownIds.has(appointment.id) ? 0 : 2) + (isFuture(appointment) ? 0 : 1);

    // Closest to now first: upcoming ones ascending, past ones descending. Sorting a copy, so the
    // source arrays are never mutated.
    const ordered = [...candidates].sort((a, b) => {
      const rankDiff = rank(a) - rank(b);

      if (rankDiff !== 0) {
        return rankDiff;
      }

      const startDiff = toMinutes(a.from) - toMinutes(b.from);

      return isFuture(a) ? startDiff : -startDiff;
    });

    return ordered.at(0)?.id;
  });

  /** Ids of the logged-in user's appointments on the loaded day. `myDay` is a plain input, so it is mirrored into a signal. */
  private readonly myDayIds = signal<ReadonlySet<string>>(new Set());

  /** Guards the guided tour auto-scroll so it fires once per arrival of the anchored appointment. */
  private hasScrolledToGuidedTourAppointment = false;

  protected readonly currentNumberOfOperatories = computed(() =>
    this.operatoriesSignal().length > 0 ? this.operatoriesSignal().length : DEFAULT_NUMBER_OF_OPERATORIES,
  );

  protected readonly calendarGridTemplateColumns = computed(() => {
    const allGaps = COLUMN_GAP * (this.currentNumberOfOperatories() - 1);
    const minWidth = this.isCompactViewSignal() ? COMPACT_VIEW_MIN_WIDTH_OF_OPERATORIES : MIN_WIDTH_OF_OPERATORIES;
    const operatoryColumnWidth = Math.max(
      minWidth,
      (this.containerWidth() - SCROLL_INDICATOR_WIDTH - allGaps) / this.currentNumberOfOperatories(),
    );
    return `repeat(${this.currentNumberOfOperatories()}, ${operatoryColumnWidth}px)`;
  });
  protected calendarGridTemplateRows = '';
  protected timeGridTemplateRows = '';

  protected readonly showcaseScrollStartHour = SHOWCASE_SCROLL_START_HOUR;
  protected readonly defaultStartHour = DEFAULT_START_HOUR;
  protected readonly intervalCount = INTERVAL_COUNT;
  protected readonly hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers, unicorn/consistent-function-scoping
  protected readonly minutesInTimeline = Array.from({ length: 60 / 10 }, (_, i) => i * 10);

  protected numberOfColumnLoadingSkeletons = NUMBER_OF_COLUMN_LOADING_SKELETONS;
  protected loadingGridTemplateColumns = `repeat(${DEFAULT_NUMBER_OF_OPERATORIES}, 1fr)`;
  protected columnLoadingSkeletons = Array.from({ length: this.numberOfColumnLoadingSkeletons }).fill(0);
  protected readonly rowLoadingSkeletons = Array.from({ length: NUMBER_OF_ROW_LOADING_SKELETONS }).fill(0);
  protected updateCount = signal(0);
  protected operatoriesSignal = signal<ScheduleCalendarVM[]>([]);
  protected isScrollAtEnd = signal(false);
  private clickDebounceTimeoutId: number | null = null;

  private readonly siteTimezone = this.store.selectSignal(selectSiteSettingsTimezone);

  @ViewChild('overflowRef') public readonly scheduleOverflowRef!: ElementRef<HTMLDivElement>;
  @ViewChild('scheduleGridRef') public readonly scheduleGridRef!: ElementRef<HTMLDivElement>;
  @ViewChild('scrollGradientRef') public readonly scrollGradientRef!: ElementRef<HTMLDivElement>;

  protected readonly currentTimeWithTimeZone = signal(this.getCurrentTimeWithTimeZone());

  private getCurrentTimeWithTimeZone(): { hour: number; minute: number } {
    const time = DateTime.local().setZone('local');
    return {
      hour: time.hour + 1,
      minute: time.minute,
    };
  }
  protected readonly timePassedOfHourInPercent$ = adjustedMinuteTimer$.pipe(
    startWith(0),
    map(() => {
      this.updateCount.update((count) => count + 1);
      this.currentTimeWithTimeZone.set(this.getCurrentTimeWithTimeZone());
      return this.calculateTimePassedOfHourInPercent(this.currentTimeWithTimeZone());
    }),
  );
  protected readonly bodySubGridTemplate = `col-start-1 col-end-[-1] row-start-2 row-end-[-1] grid grid-cols-subgrid grid-rows-subgrid`;
  protected TagEvent = TagEvent;

  public containerHeight = signal((END_HOUR - START_HOUR) * INTERVAL_COUNT * ROW_HEIGHT);

  constructor() {
    //header + rows
    this.calendarGridTemplateRows = `max-content repeat(${(END_HOUR - START_HOUR) * INTERVAL_COUNT} , ${ROW_HEIGHT_PX})`;
    this.timeGridTemplateRows = `repeat(${(END_HOUR - START_HOUR) * TIME_COLUMN_INTERVAL_COUNT} , 21px)`;

    this.activatedRoute.params
      .pipe(
        map((params) => params),
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.updateCount.set(0);
        this.schedulesService.removeAppointmentSelection();
        this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
      });

    effect(() => {
      // only after page reload and only after the first update, this ensures timeIndicator element is rendered
      if (this.updateCount() === 1) {
        this.scrollIntoView(this.isToday);
      }
    });

    effect(() => {
      // Trigger scroll position check when grid columns change
      this.calendarGridTemplateColumns();
      this.checkScrollPosition();
    });

    this.setupGuidedTourAutoScroll();
  }

  public ngAfterViewInit(): void {
    this.myDayCalendarItems.changes.subscribe(() => {
      this.checkWidths();
    });
    this.onCalendarViewWidthChange();
    this.setupScrollListener();
  }

  public checkWidths(): void {
    this.myDayCalendarItems.forEach((item) => {
      if (item.elementRef.nativeElement) {
        const width = item.elementRef.nativeElement.offsetWidth;
        if (width === 0) {
          // remove outline highlight when selecting an appointment which is not shown in myDay (we only show 2 parallel appointments)
          this.renderer.addClass(item.elementRef.nativeElement, '!outline-0');
        }
      }
    });
  }

  public ngOnChanges(changes: TypedChanges<ScheduleCalendarComponent>): void {
    this.currentTimeWithTimeZone.set(this.getCurrentTimeWithTimeZone());
    if (changes['operatories']) {
      this.operatoriesSignal.set(this.operatories);
    }
    if (changes['myDay']) {
      this.myDayIds.set(new Set(this.myDay.map((appointment) => appointment.id)));
    }
  }

  public ngOnDestroy(): void {
    if (this.clickDebounceTimeoutId !== null) {
      clearTimeout(this.clickDebounceTimeoutId);
      this.clickDebounceTimeoutId = null;
    }
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
  }

  public isSafari(): boolean {
    // exclude Chrome, Edge, and all Android browsers that include the Safari name in their user agent.
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  /**
   * True for the one appointment the guided tour is anchored to. While an appointment is selected, the
   * right sidebar is open and the coachmark moves to its Chart button, so none is anchored here.
   */
  protected isGuidedTourAppointment(appointmentId: string): boolean {
    return !this.selectedAppointmentId && appointmentId === this.guidedTourAppointmentId();
  }

  /**
   * True while the guided tour coachmark is actually showing on this appointment, i.e. it is the
   * anchored appointment AND the step has not been dismissed/hidden. Used to suppress the regular
   * hover tooltip, which would otherwise overlap the coachmark.
   */
  protected isGuidedTourAppointmentActive(appointmentId: string): boolean {
    return this.isGuidedTourAppointment(appointmentId) && this.nurturingStore.visibleNurturingFeatures().includes(this.guidedTourFeature);
  }

  /**
   * Brings the guided tour's appointment into view when it is on the loaded day, outside the viewport and
   * its step is still enabled, so the coachmark is not left anchored off-screen. Runs once per arrival of that
   * appointment: `hasScrolledToGuidedTourAppointment` keeps a later re-render (or the user scrolling
   * away deliberately) from yanking the viewport back.
   */
  private setupGuidedTourAutoScroll(): void {
    effect(() => {
      const isStepEnabled = this.nurturingStore.visibleNurturingFeatures().includes(this.guidedTourFeature);
      // The candidate is picked from the loaded grid, so having an id already means it is on this day.
      const appointmentId = this.guidedTourAppointmentId();

      if (!isStepEnabled || appointmentId === undefined) {
        // Reset so switching away from the day and back scrolls again.
        this.hasScrolledToGuidedTourAppointment = false;
        return;
      }

      if (this.hasScrolledToGuidedTourAppointment) {
        return;
      }

      this.hasScrolledToGuidedTourAppointment = true;

      // The appointment element only exists after the grid has rendered this day's data.
      afterNextRender(
        () => {
          this.scrollToGuidedTourAppointment(appointmentId);
        },
        { injector: this.injector },
      );
    });
  }

  /**
   * Scrolls the anchored appointment into view leaving {@link GUIDED_TOUR_SCROLL_OFFSET_Y} of headroom
   * above it, so the coachmark — which prefers sitting above its anchor — has room to do so instead
   * of being flipped below. Deliberately not reusing {@link scrollToAppointment}, whose smaller
   * offset is tuned for appointment selection.
   */
  private scrollToGuidedTourAppointment(appointmentId: string): void {
    const container = this.scheduleOverflowRef?.nativeElement;
    const item = this.allCalendarItems?.find((entry) => entry.vm?.id === appointmentId);
    const element = item?.elementRef?.nativeElement;

    if (!container || !element) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const isInViewport =
      elementRect.top >= containerRect.top &&
      elementRect.bottom <= containerRect.bottom &&
      elementRect.left >= containerRect.left &&
      elementRect.right <= containerRect.right;

    if (isInViewport) {
      return;
    }

    container.scrollTo({
      top: container.scrollTop + (elementRect.top - containerRect.top) - GUIDED_TOUR_SCROLL_OFFSET_Y,
      left: container.scrollLeft + (elementRect.left - containerRect.left) - GUIDED_TOUR_SCROLL_OFFSET_X,
      behavior: 'smooth',
    });
  }

  /**
   * "Next" on the guided tour coachmark: permanently dismisses this step, then opens the chart of
   * the patient on the anchored appointment, where the tour continues.
   */
  protected onGuidedTourNext(appointment: ScheduleCalendarElement): void {
    this.nurturingStore.dismissNurturingFeature(this.guidedTourFeature);

    if (!appointment.patientId || !appointment.siteId) {
      return;
    }

    this.navigationService.navigate('chartingOnSiteForPatient', false, [appointment.siteId, appointment.patientId]);
  }

  protected onCalendarItemClick(appointmentId: string): void {
    if (this.isEditAppointment()) return;
    // Reset any unfinished new-appointment draft BEFORE selecting, so the selection set by
    // setAppointmentId (onSetSelectedAppointment) is not wiped by onResetCreateAppointment.
    // Selecting last keeps selectedAppointment populated for the edit/delete flows (FW10-3798, FW10-3802).
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    this.appointmentSelect.emit(appointmentId);
    if (this.clickDebounceTimeoutId !== null) {
      clearTimeout(this.clickDebounceTimeoutId);
    }
    this.clickDebounceTimeoutId = window.setTimeout(() => {
      this.clickDebounceTimeoutId = null;
      this.appointmentClick.emit(appointmentId);
    }, CLICK_DEBOUNCE_MS);
  }

  protected onCalendarItemDoubleClick(): void {
    if (this.clickDebounceTimeoutId !== null) {
      clearTimeout(this.clickDebounceTimeoutId);
      this.clickDebounceTimeoutId = null;
    }
    if (this.isEditAppointment()) return;
    this.appointmentDoubleClick.emit();
  }

  protected onEventClick(eventId: string): void {
    if (this.isEditAppointment()) return;
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    this.eventClick.emit(eventId);
  }

  public onTimeSlotClick({ hour, minute, operatory }: ScheduleCalendarTimeWithOperatory): void {
    if (this.isEditAppointment() || this.isEaglesoftPms()) return;
    this.newAppointment.emit({ hour, minute, operatory });
  }

  public columnTemplateCount(index: number, myDay: ScheduleCalendarElement[] | undefined): number {
    const myDayColOffset = 0;
    return index + myDayColOffset + (myDay ? 1 : 0);
  }

  public calculateTimePassedOfHourInPercent(currentTime: ScheduleCalendarTime): number {
    return (currentTime.minute / 60) * 100;
  }

  public scrollToCurrentTime(): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollToCurrentTime();
  }

  public scrollToShowcaseStartHour(): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollToShowcaseStartHour();
  }

  public scrollToDefaultStartHour(): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollToDefaultStartHour();
  }

  /**
   * Scrolls the calendar to the current time or a specific hour based on the provided parameters.
   * @param isToday - A boolean indicating whether to scroll to the current time or not.
   */
  public scrollIntoView(isToday?: boolean): void {
    this.rowsSeparatorsAndTimeIndicator?.scrollIntoView(isToday);
  }

  /**
   * Scrolls the calendar to show the selected appointment.
   * @param appointmentId - The ID of the appointment to scroll to.
   */
  public scrollToAppointment(appointmentId: string): void {
    if (!this.scheduleOverflowRef?.nativeElement || !this.allCalendarItems) return;

    // Find the appointment component by ID
    const appointmentComponent = this.allCalendarItems.find((item) => item.vm?.id === appointmentId);

    if (appointmentComponent?.elementRef?.nativeElement) {
      const offsetY = 100;
      const offsetX = 100;
      const containerRect = this.scheduleOverflowRef.nativeElement.getBoundingClientRect();
      const elementRect = appointmentComponent.elementRef.nativeElement.getBoundingClientRect();
      const scrollTop = this.scheduleOverflowRef.nativeElement.scrollTop + (elementRect.top - containerRect.top);
      const scrollLeft = this.scheduleOverflowRef.nativeElement.scrollLeft + (elementRect.left - containerRect.left);

      this.scheduleOverflowRef.nativeElement.scrollTo({
        top: scrollTop - offsetY,
        left: scrollLeft - offsetX,
        behavior: 'smooth',
      });
    }
  }

  public appointmentHasParallelAppointments(appointment: ScheduleCalendarElement): boolean {
    return this.myDay ? appointmentHasParallelAppointments(appointment, this.myDay) : false;
  }

  protected onOpenHealthHistory(element: ScheduleCalendarElement): void {
    if (!element.patientId || !element.siteId) return;
    this.store.dispatch(
      patientActions.onOpenHealthHistory({
        patientId: element.patientId,
      }),
    );
    this.dialog.open<HealthHistoryDialogComponent, HealthHistoryDialogData>(HealthHistoryDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[1356px]', 'h-full', 'pt-[84px]', 'pb-[12px]'],
      data: {
        siteId: element.siteId,
        patientId: element.patientId,
      },
    });
  }

  public onScheduleEditClick(element: ScheduleCalendarElement): void {
    this.store.dispatch(createAppointmentActions.onSetIsEditAppointment({ isShowcase: element.siteId === routes.showcaseSiteId }));
    this.schedulesService.editSelectedAppointment({ isEdited: true });
  }

  public onDeleteClick(_element: ScheduleCalendarElement): void {
    this.dialog
      .open<DeleteAppointmentModalComponentResult, DeleteAppointmentModalComponentData>(DeleteAppointmentModalComponent, {
        backdropClass: ['bg-overlay-light'],
        panelClass: ['w-[480px]'],
        disableClose: true,
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.isDeleted) {
          this.schedulesService.deleteAppointment();
        }
      });
  }

  public onChangeStatus({ element, statusId }: { element: ScheduleCalendarElement; statusId: string }): void {
    const status = this.statusOptions().find((option) => option.id === statusId);
    const originalStatus = element.statusId ? this.statusOptions().find((option) => option.id === element.statusId) : undefined;

    if (!status) {
      return;
    }

    const isCompletedStatus = status.description.toLowerCase().includes('completed');

    if (isCompletedStatus) {
      this.schedulesService
        .getAppointmentDataWithProcedures$(element.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((selectedAppointment) => {
          if (selectedAppointment?.appointment.procedures && selectedAppointment.appointment.procedures.length > 0) {
            this.loadTeethAndOpenModal(element, selectedAppointment);
          } else {
            this.schedulesService.updateAppointmentStatus(element.id, status, element.isTentative ?? false, originalStatus);
          }
        });
      return;
    }

    this.schedulesService.updateAppointmentStatus(element.id, status, element.isTentative ?? false, originalStatus);
  }

  public onCompleteAppointment(element: ScheduleCalendarElement): void {
    this.schedulesService
      .getAppointmentDataWithProcedures$(element.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedAppointment) => {
        this.loadTeethAndOpenModal(element, selectedAppointment);
      });
  }

  private loadTeethAndOpenModal(
    element: ScheduleCalendarElement,
    selectedAppointment: ScheduleOperatoryProviderAppointment | undefined,
  ): void {
    if (!selectedAppointment) {
      return;
    }

    const patientId = element.patientId!;
    const siteId = element.siteId!;

    this.store.dispatch(
      patientActions.onLoadTeeth({
        patientId,
        siteId,
        examCount: 5,
        silentReload: true,
      }),
    );

    this.store
      .select(selectTeeth)
      .pipe(
        filter((teeth) => teeth?.teethWithLayers !== undefined),
        take(1),
        switchMap((teethData) => {
          const patient: PatientDetailsV2 = {
            id: patientId,
            firstName: element.patientName.split(' ')[0] ?? '',
            lastName: element.patientName.split(' ').slice(1).join(' ') ?? '',
          } as PatientDetailsV2;

          return this.completeAppointmentService.openCompleteAppointmentModal$({
            patient,
            appointmentDate: DateTime.fromISO(element.date),
            procedures: selectedAppointment.appointment.procedures,
            progressNotes: selectedAppointment.appointment.progressNotes,
            teeth: teethData?.teethWithLayers ?? [],
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((completedTreatments) => {
        this.handleCompletionResult(completedTreatments, element, selectedAppointment, patientId, siteId);
      });
  }

  private handleCompletionResult(
    completedTreatments: AddTreatmentItem[] | undefined,
    element: ScheduleCalendarElement,
    selectedAppointment: ScheduleOperatoryProviderAppointment,
    patientId: string,
    siteId: string,
  ): void {
    // Empty array or non-empty array means user clicked "Set to Complete"
    if (Array.isArray(completedTreatments) && completedTreatments.length > 0) {
      this.completeAppointmentService.completeTreatments({
        treatments: completedTreatments,
        patientId,
        siteId,
        provider: selectedAppointment.appointment.provider,
        appointmentDate: selectedAppointment.appointment.from,
      });
    }

    // Update appointment status to completed, Eaglesoft appointment statuses are read-only
    const completedStatus = this.statusOptions().find((s) => s.description.toLowerCase().includes('completed'));
    if (completedStatus && completedTreatments !== undefined && !this.isEaglesoftPms()) {
      this.schedulesService.updateAppointmentStatus(element.id, completedStatus, element.isTentative ?? false);
    }
  }

  private readonly onCalendarViewWidthChange = (): void => {
    if (this.viewContainerRef) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width - TIME_COLUMN_WIDTH - MY_DAY_COLUMN_WIDTH;
          this.containerWidth.set(width);
          this.cdr.detectChanges();
        }
      });
      resizeObserver.observe(this.viewContainerRef?.element.nativeElement);
    }
  };

  private setupScrollListener(): void {
    if (this.scheduleOverflowRef) {
      this.scheduleOverflowRef.nativeElement.addEventListener('scroll', () => {
        this.checkScrollPosition();
      });
    }
  }

  private checkScrollPosition(): void {
    if (this.scheduleOverflowRef) {
      const element = this.scheduleOverflowRef.nativeElement;
      const offsetY = 10;
      const isAtEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - offsetY; // -offsetY for potential rounding errors
      this.isScrollAtEnd.set(isAtEnd);
    }
  }
}
